/**
 * HTML Sanitization Utility
 * Sanitizes HTML content to prevent XSS attacks
 * Uses DOMPurify-like approach for client-side sanitization
 */

/**
 * Allowed HTML tags for rich text editor
 */
const ALLOWED_TAGS = [
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'strike',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'ul',
  'ol',
  'li',
  'blockquote',
  'a',
  'span',
  'div',
  'code',
  'pre',
]

/**
 * Allowed HTML attributes
 */
const ALLOWED_ATTRIBUTES = {
  a: ['href', 'title', 'target'],
  span: ['style'],
  div: ['style'],
  p: ['style'],
  h1: ['style'],
  h2: ['style'],
  h3: ['style'],
  h4: ['style'],
  h5: ['style'],
  h6: ['style'],
}

/**
 * Allowed URL protocols
 */
const ALLOWED_PROTOCOLS = ['http:', 'https:', 'mailto:']

/**
 * Sanitize HTML string
 */
export const sanitizeHTML = (html) => {
  if (typeof html !== 'string') {
    return ''
  }

  // Create a temporary DOM element
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = html

  // Recursively sanitize the DOM tree
  sanitizeNode(tempDiv)

  return tempDiv.innerHTML
}

/**
 * Sanitize a DOM node recursively
 */
const sanitizeNode = (node) => {
  // Handle text nodes
  if (node.nodeType === Node.TEXT_NODE) {
    return
  }

  // Handle element nodes
  if (node.nodeType === Node.ELEMENT_NODE) {
    const tagName = node.tagName.toLowerCase()

    // Remove disallowed tags
    if (!ALLOWED_TAGS.includes(tagName)) {
      // Replace with text content
      const textNode = document.createTextNode(node.textContent)
      node.parentNode?.replaceChild(textNode, node)
      return
    }

    // Remove disallowed attributes
    const allowedAttrs = ALLOWED_ATTRIBUTES[tagName] || []
    const attributesToRemove = []

    for (let i = 0; i < node.attributes.length; i++) {
      const attr = node.attributes[i]
      const attrName = attr.name.toLowerCase()

      // Remove event handlers
      if (attrName.startsWith('on')) {
        attributesToRemove.push(attrName)
        continue
      }

      // Remove disallowed attributes
      if (!allowedAttrs.includes(attrName)) {
        attributesToRemove.push(attrName)
        continue
      }

      // Sanitize href/src attributes
      if ((attrName === 'href' || attrName === 'src') && attr.value) {
        const url = new URL(attr.value, window.location.href)
        if (!ALLOWED_PROTOCOLS.includes(url.protocol)) {
          attributesToRemove.push(attrName)
          continue
        }
        // Update with sanitized URL
        node.setAttribute(attrName, url.toString())
      }

      // Sanitize style attributes
      if (attrName === 'style') {
        const sanitizedStyle = sanitizeStyle(attr.value)
        if (sanitizedStyle) {
          node.setAttribute(attrName, sanitizedStyle)
        } else {
          attributesToRemove.push(attrName)
        }
      }
    }

    // Remove disallowed attributes
    attributesToRemove.forEach((attrName) => {
      node.removeAttribute(attrName)
    })

    // Recursively sanitize child nodes
    const children = Array.from(node.childNodes)
    children.forEach((child) => {
      sanitizeNode(child)
    })
  }
}

/**
 * Sanitize CSS style string
 */
const sanitizeStyle = (style) => {
  if (typeof style !== 'string') {
    return ''
  }

  // Remove dangerous CSS properties
  const dangerousProperties = [
    'expression',
    'javascript:',
    'vbscript:',
    'onload',
    'onerror',
    'import',
    'url(javascript:',
    'url(vbscript:',
  ]

  let sanitized = style
  dangerousProperties.forEach((prop) => {
    const regex = new RegExp(prop.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi')
    sanitized = sanitized.replace(regex, '')
  })

  // Remove @import statements
  sanitized = sanitized.replace(/@import[^;]+;/gi, '')

  return sanitized.trim()
}

/**
 * Sanitize URL
 */
export const sanitizeURL = (url) => {
  if (typeof url !== 'string') {
    return ''
  }

  try {
    const parsedUrl = new URL(url, window.location.href)

    // Only allow safe protocols
    if (!ALLOWED_PROTOCOLS.includes(parsedUrl.protocol)) {
      return ''
    }

    // Remove javascript: and data: protocols
    if (parsedUrl.protocol === 'javascript:' || parsedUrl.protocol.startsWith('data:')) {
      return ''
    }

    return parsedUrl.toString()
  } catch (e) {
    // Invalid URL
    return ''
  }
}

/**
 * Strip all HTML tags
 */
export const stripHTML = (html) => {
  if (typeof html !== 'string') {
    return ''
  }

  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = html
  return tempDiv.textContent || tempDiv.innerText || ''
}

/**
 * Escape HTML entities
 */
export const escapeHTML = (str) => {
  if (typeof str !== 'string') {
    return ''
  }

  const entityMap = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
    '/': '&#x2F;',
  }

  return str.replace(/[&<>"'/]/g, (s) => entityMap[s])
}

/**
 * Sanitize React Quill content
 */
export const sanitizeQuillContent = (content) => {
  if (!content) {
    return ''
  }

  // If content is a Delta object, convert to HTML first
  if (typeof content === 'object' && content.ops) {
    // React Quill Delta format - convert to HTML
    // This is a simplified conversion
    let html = ''
    content.ops.forEach((op) => {
      if (typeof op.insert === 'string') {
        let text = op.insert
        const attrs = op.attributes || {}

        if (attrs.bold) text = `<strong>${text}</strong>`
        if (attrs.italic) text = `<em>${text}</em>`
        if (attrs.underline) text = `<u>${text}</u>`
        if (attrs.strike) text = `<s>${text}</s>`

        if (attrs.header) {
          text = `<h${attrs.header}>${text}</h${attrs.header}>`
        } else if (attrs.list === 'ordered') {
          text = `<li>${text}</li>`
        } else if (attrs.list === 'bullet') {
          text = `<li>${text}</li>`
        } else if (attrs.link) {
          const sanitizedLink = sanitizeURL(attrs.link)
          text = sanitizedLink ? `<a href="${sanitizedLink}">${text}</a>` : text
        }

        html += text
      }
    })
    return sanitizeHTML(html)
  }

  // If content is already HTML string
  if (typeof content === 'string') {
    return sanitizeHTML(content)
  }

  return ''
}

export default {
  sanitizeHTML,
  sanitizeURL,
  stripHTML,
  escapeHTML,
  sanitizeQuillContent,
}

